// ============================================================================
// noticeWidget — materialises the compact Notice widget config into real fields
// ============================================================================
// A section authored as `{ type: 'notice', notice: {…} }` (or carrying a
// `notice` config) is expanded here into a standard SectionSpec whose
// `fields[]` are ordinary FieldSpecs. This keeps ONE code path for validation,
// value plumbing and collectAllFields, while `render_as: 'notice'` tells
// DynamicForm to draw the expanded fields with the <NoticeSection> widget UI.
//
// The win: the recipient service/delivery grid, publications, and
// reply/rejoinder grids — ~170 lines of repeated JSON per notice — collapse to
// a handful of flags. Notice-specific scalars (number, date, amounts) stay
// explicit in `detail_fields`.

import type {
  FieldSpec,
  FormSpec,
  GridFieldSpec,
  GridRowFieldSpec,
  NoticeConfig,
  SectionSpec,
} from 'api/types/formSpec.types';
import { expandCaseStatusSection, isCaseStatusSection } from './caseStatusWidget';
import {
  expandAccountsSection, isAccountsSection,
  expandRespondentsSection, isRespondentsSection,
  expandCaseDetailsSection, isCaseDetailsSection,
} from './embeddedWidgets';

const DEFAULT_ROLES = ['PRIMARY', 'CO_BORROWER', 'GUARANTOR'];

// Built-in "common to almost every notice" fields. A workflow references these
// by key via `standard_fields` and only authors its own extras in
// `custom_fields`, so notice number/date/amounts are never re-declared.
// `source_hint` + `editable` let the form pre-fill from the account snapshot
// while staying editable; `denorm_to` keeps the reporting snapshot columns fed.
const STANDARD_NOTICE_FIELDS: Record<string, FieldSpec> = {
  notice_number: {
    key: 'notice_number', type: 'text', label: 'Notice Number',
    required: true, max_length: 60, denorm_to: 'status_value',
  } as unknown as FieldSpec,
  notice_date: {
    key: 'notice_date', type: 'date', label: 'Notice Date',
    required: true, denorm_to: 'event_date',
  } as unknown as FieldSpec,
  outstanding_amount: {
    key: 'outstanding_amount', type: 'number', label: 'Outstanding Amount (₹)',
    required: true, min: 0, source_hint: 'account.current_outstanding',
    editable: true, denorm_to: 'amount_primary',
  } as unknown as FieldSpec,
  overdue_amount: {
    key: 'overdue_amount', type: 'number', label: 'Overdue Amount (₹)',
    min: 0, source_hint: 'account.current_overdue_amount',
    editable: true, denorm_to: 'amount_secondary',
  } as unknown as FieldSpec,
  amount_demanded: {
    key: 'amount_demanded', type: 'number', label: 'Amount Demanded (₹)',
    required: true, min: 0, source_hint: 'account.current_outstanding',
    editable: true, denorm_to: 'amount_primary',
  } as unknown as FieldSpec,
  due_date: {
    key: 'due_date', type: 'date', label: 'Payment Due Date', required: true,
  } as unknown as FieldSpec,
};

const DEFAULT_STANDARD_FIELDS = ['notice_number', 'notice_date', 'outstanding_amount', 'overdue_amount'];

// Resolve the notice-detail field list: standard set (by key) + custom extras,
// with a legacy fallback to `detail_fields`.
function resolveDetailFields(cfg: NoticeConfig): FieldSpec[] {
  const out: FieldSpec[] = [];

  const stdKeys = cfg.standard_fields
    ?? (cfg.detail_fields || cfg.custom_fields ? [] : DEFAULT_STANDARD_FIELDS);
  for (const key of stdKeys) {
    const f = STANDARD_NOTICE_FIELDS[key];
    if (f) out.push(f);
    // Unknown standard keys are silently skipped — author them as custom_fields.
  }

  if (cfg.custom_fields) out.push(...cfg.custom_fields);
  if (cfg.detail_fields) out.push(...cfg.detail_fields); // legacy
  return out;
}

const ROLE_LABEL: Record<string, string> = {
  PRIMARY: 'Borrower',
  CO_BORROWER: 'Co-Borrower',
  GUARANTOR: 'Guarantor',
};

const roleOptions = (roles: string[]) =>
  roles.map((r) => ({ value: r, label: ROLE_LABEL[r] ?? r }));

// ---- Recipient service & delivery grid --------------------------------------

function recipientsGrid(cfg: NoticeConfig): GridFieldSpec {
  const roles = cfg.recipient_roles && cfg.recipient_roles.length > 0
    ? cfg.recipient_roles
    : DEFAULT_ROLES;

  const rowFields: GridRowFieldSpec[] = [
    { key: 'party_role', type: 'select', label: 'Role', width: '130px', options: roleOptions(roles) },
    { key: 'recipient_name', type: 'text', label: 'Recipient', required: true, max_length: 200 },
    { key: 'recipient_address', type: 'textarea', label: 'Address', max_length: 500 },
  ];

  if (cfg.service_tracking !== false) {
    rowFields.push(
      { key: 'dispatch_mode', type: 'select', label: 'Mode', options: [
        { value: 'RPAD', label: 'RPAD (Registered AD)' },
        { value: 'SPEED_POST', label: 'Speed Post' },
        { value: 'COURIER', label: 'Courier' },
        { value: 'EMAIL', label: 'Email' },
        { value: 'SMS', label: 'SMS' },
        { value: 'HAND_DELIVERY', label: 'Hand Delivery' },
      ] },
      { key: 'dispatch_date', type: 'date', label: 'Dispatched On' },
      { key: 'tracking_no', type: 'text', label: 'Tracking #', max_length: 60 },
      { key: 'delivery_status', type: 'select', label: 'Delivery', options: [
        { value: 'DISPATCHED', label: 'Dispatched' },
        { value: 'DELIVERED', label: 'Delivered' },
        { value: 'SERVED', label: 'Served' },
        { value: 'RETURNED', label: 'Returned' },
        { value: 'REFUSED', label: 'Refused' },
        { value: 'NOT_TRACEABLE', label: 'Not Traceable' },
      ] },
      { key: 'delivery_date', type: 'date', label: 'Delivery / Return Date',
        show_if: "delivery_status != null && delivery_status != 'DISPATCHED'" },
      { key: 'pod_remarks', type: 'textarea', label: 'POD / Remarks', max_length: 500 },
    );
  }

  return {
    key: 'recipients',
    type: 'grid',
    label: 'Recipients',
    required: true,
    min_rows: cfg.min_recipients ?? 1,
    max_rows: 20,
    add_label: 'Add Recipient',
    seed_from: 'case_parties',
    seed_label: 'Pre-fill from account',
    seed_dedupe_key: 'party_id',
    empty_message: "No recipients yet. Use 'Pre-fill from account' to populate from the loan.",
    row_stepper: cfg.service_tracking !== false
      ? ['recipient_name', 'dispatch_date', 'delivery_status']
      : ['recipient_name'],
    row_fields: rowFields,
  } as unknown as GridFieldSpec;
}

// ---- Optional feature grids -------------------------------------------------

function publicationsGrid(): GridFieldSpec {
  return {
    key: 'publications',
    type: 'grid',
    label: 'Newspaper Publications',
    description: 'Required only if service failed on any recipient. Add one row per publication.',
    min_rows: 0,
    max_rows: 10,
    add_label: 'Add Publication',
    empty_message: 'No publications recorded.',
    row_fields: [
      { key: 'newspaper_name', type: 'text', label: 'Newspaper', required: true, max_length: 200 },
      { key: 'edition', type: 'text', label: 'Edition', max_length: 100 },
      { key: 'publication_date', type: 'date', label: 'Publication Date', required: true },
      { key: 'remarks', type: 'textarea', label: 'Remarks', max_length: 500 },
    ] as GridRowFieldSpec[],
  } as unknown as GridFieldSpec;
}

function repliesGrid(): GridFieldSpec {
  return {
    key: 'borrower_replies',
    type: 'grid',
    label: 'Borrower Replies',
    description: 'The From dropdown lists the recipients captured above.',
    min_rows: 0,
    max_rows: 20,
    add_label: 'Add Reply',
    empty_message: 'No replies recorded.',
    row_fields: [
      { key: 'recipient_ref', type: 'select', label: 'From', required: true,
        options_source: 'form_field', options_source_field: 'recipients',
        options_source_label: 'recipient_name', options_source_value: 'recipient_name' },
      { key: 'reply_date', type: 'date', label: 'Reply Date', required: true },
      { key: 'reply_mode', type: 'select', label: 'Mode', options: [
        { value: 'WRITTEN', label: 'Written' },
        { value: 'EMAIL', label: 'Email' },
        { value: 'LEGAL_NOTICE', label: 'Through Legal Notice' },
        { value: 'OTHER', label: 'Other' },
      ] },
      { key: 'reply_details', type: 'textarea', label: 'Reply Content', max_length: 3000 },
    ] as unknown as GridRowFieldSpec[],
  } as unknown as GridFieldSpec;
}

function rejoindersGrid(): GridFieldSpec {
  return {
    key: 'company_replies',
    type: 'grid',
    label: 'Company Rejoinders',
    description: 'The Reply To dropdown lists the recipients captured above.',
    min_rows: 0,
    max_rows: 20,
    add_label: 'Add Rejoinder',
    empty_message: 'No rejoinders recorded.',
    row_fields: [
      { key: 'reply_to', type: 'select', label: 'Reply To', required: true,
        options_source: 'form_field', options_source_field: 'recipients',
        options_source_label: 'recipient_name', options_source_value: 'recipient_name' },
      { key: 'reply_date', type: 'date', label: 'Reply Date', required: true },
      { key: 'reply_details', type: 'textarea', label: 'Reply Content', max_length: 3000 },
    ] as unknown as GridRowFieldSpec[],
  } as unknown as GridFieldSpec;
}

// ---- Public API -------------------------------------------------------------

/**
 * Expand a notice-widget section into a plain SectionSpec with real fields[]
 * plus a `render_as: 'notice'` marker + the retained notice config (for the
 * widget's header / Generate button).
 */
export function expandNoticeSection(section: SectionSpec): SectionSpec {
  const cfg: NoticeConfig = section.notice ?? {};

  const fields: FieldSpec[] = [];
  // 1. Notice-detail scalars — standard common fields + workflow custom fields.
  fields.push(...resolveDetailFields(cfg));
  // 2. Recipient service & delivery grid.
  fields.push(recipientsGrid(cfg));
  // 3. Optional blocks.
  if (cfg.publication) fields.push(publicationsGrid());
  if (cfg.replies)     fields.push(repliesGrid());
  if (cfg.rejoinders)  fields.push(rejoindersGrid());
  // 4. Remarks (default on).
  if (cfg.remarks !== false) {
    fields.push({ key: 'remarks', type: 'textarea', label: 'Remarks', max_length: 2000 } as FieldSpec);
  }

  return {
    key: section.key,
    title: cfg.title ?? section.title ?? 'Notice',
    description: cfg.description ?? section.description,
    layout: 'auto',
    fields,
    render_as: 'notice',
    notice: cfg,
  };
}

const isNoticeSection = (s: SectionSpec): boolean =>
  s.type === 'notice' || s.render_as === 'notice' || !!s.notice;

/**
 * Walk a FormSpec and expand every reusable widget section (notice,
 * case_status_update). Idempotent. Returns the same object identity when
 * nothing needs expanding.
 */
export function materializeFormSpec(spec: FormSpec | null): FormSpec | null {
  if (!spec || !spec.sections || spec.sections.length === 0) return spec;
  const needsWork = spec.sections.some((s) =>
    isNoticeSection(s) || isCaseStatusSection(s) || isAccountsSection(s)
    || isRespondentsSection(s) || isCaseDetailsSection(s));
  if (!needsWork) return spec;
  return {
    ...spec,
    sections: spec.sections.map((s) =>
      isNoticeSection(s) ? expandNoticeSection(s)
        : isCaseStatusSection(s) ? expandCaseStatusSection(s)
        : isAccountsSection(s) ? expandAccountsSection(s)
        : isRespondentsSection(s) ? expandRespondentsSection(s)
        : isCaseDetailsSection(s) ? expandCaseDetailsSection(s)
        : s),
  };
}
